import { resolveText } from "../../i18n/resolve-text.js";
import { resolveLabel } from "../utils/resolve-label.js";
import { badgeText, playingText, seekToEdgeText } from "../../../i18n/text/live.js";
import { createState } from "@videojs/store";
import { defaults } from "@videojs/utils/object";
//#region src/core/ui/live-button/core.ts
/**
* Fallback offset (in seconds) from the end of the seekable window used to decide "at live edge" when `liveEdgeStart`
* is unavailable.
*/
const LIVE_EDGE_OFFSET = 10;
/**
* Grace window (in seconds) before `liveEdgeStart` that still counts as "at the live edge". Absorbs the small gap
* between the player's initial playback position (e.g. hls.js `liveSyncDuration`) and the manifest's `HOLD-BACK`, so
* autoplay reliably reports live.
*/
const LIVE_EDGE_TOLERANCE = 5;
/**
* Core state machine for a "Live" button. Indicates whether the player is playing at the live edge and seeks to the
* Seekable Live Edge when activated.
*
* @internal
* @see https://github.com/video-dev/media-ui-extensions/blob/main/proposals/0007-live-edge.md
*/
var LiveButtonCore = class LiveButtonCore {
	/** Default visible text used when no children are provided. */
	static defaultText = badgeText;
	static defaultProps = {
		label: "",
		disabled: false
	};
	state = createState({
		live: false,
		liveEdge: false,
		disabled: true,
		label: ""
	});
	#props = { ...LiveButtonCore.defaultProps };
	#media = null;
	constructor(props) {
		if (props) this.setProps(props);
	}
	setProps(props) {
		this.#props = defaults(props, LiveButtonCore.defaultProps);
	}
	getLabel(state) {
		const label = resolveLabel(this.#props.label, state);
		if (label) return label;
		if (state.liveEdge) return playingText;
		return seekToEdgeText;
	}
	getAttrs(state) {
		const inactive = state.disabled || state.liveEdge;
		return {
			"aria-label": this.getLabel(state),
			"aria-disabled": inactive ? "true" : void 0
		};
	}
	setMedia(media) {
		this.#media = media;
	}
	getState() {
		const media = this.#media;
		const live = isLiveMedia(media);
		const liveEdge = live && this.#isAtLiveEdge(media);
		const disabled = this.#props.disabled || !live || liveEdgeTarget(media) == null;
		this.state.patch({
			live,
			liveEdge,
			disabled
		});
		this.state.patch({ label: resolveText(this.getLabel(this.state.current)) });
		return this.state.current;
	}
	/** Seek to the Seekable Live Edge. No-op when not live or already at edge. */
	async seekToLive(media) {
		this.setMedia(media);
		if (this.getState().disabled) return;
		if (!isLiveMedia(media)) return;
		if (this.#isAtLiveEdge(media)) return;
		const target = liveEdgeTarget(media);
		if (target == null) return;
		await media.seek(target);
	}
	#isAtLiveEdge(media) {
		const { currentTime, liveEdgeStart } = media;
		if (Number.isFinite(liveEdgeStart)) return currentTime >= liveEdgeStart - LIVE_EDGE_TOLERANCE;
		const target = liveEdgeTarget(media);
		if (target == null) return false;
		return currentTime >= target - LIVE_EDGE_OFFSET;
	}
};
function isLiveMedia(media) {
	return !Number.isNaN(media.targetLiveWindow);
}
function liveEdgeTarget(media) {
	const { seekable } = media;
	if (seekable.length === 0) return null;
	const end = seekable[seekable.length - 1][1];
	return Number.isFinite(end) ? end : null;
}
//#endregion
export { LiveButtonCore };
